import React, { Dispatch, Fragment, SetStateAction } from 'react';
import styles from './TxProcessing.module.scss';
import { formatHash } from '@/src/lib/SmartDB/Commons/formatters';
import LoaderButton from '@/src/components/Commons/LoaderButton/LoaderButton';
import CopyButton from '../CopyButton/CopyButton';
import OpenInNewTabButton from '../OpenInNewTabButton/OpenInNewTabButton';
import { useAppStore } from '@/src/store/global';
import { Button } from '@/src/components/Commons/Button';
import { useRouter } from '@/src/store/router/RouterProvider';
import { ROUTES } from '@/src/utils/routes';
import { TASK } from '@/src/utils/constants';
import SuccessIcon from '../../Commons/SuccessIcon/SuccessIcon';
import FailedIcon from '../../Commons/FailedIcon/FailedIcon';

interface ITxProcessingProps {
    isProcessingTx: boolean;
    processingTxMessage: string;
    processingTxHash: string;
    isFaildedTx: boolean;
    setIsFaildedTx: Dispatch<SetStateAction<boolean>>;
    isConfirmedTx: boolean;
    setIsConfirmedTx: Dispatch<SetStateAction<boolean>>;
    onFinishTask?: (swBackToDashboard?: boolean | undefined) => Promise<void>;
    showNewTransactionButton?: boolean;
    showViewTransactionsButton?: boolean;
    nameForNewTransactionButton?: string;
    nameForCloseButton?: string;
    othersButtonsWhenConfirmed?: React.ReactNode;
    othersButtonsWhenFailed?: React.ReactNode;
}

const TxProcessing: React.FC<ITxProcessingProps> = (props) => {
    const {
        isProcessingTx,
        isFaildedTx,
        setIsFaildedTx,
        isConfirmedTx,
        setIsConfirmedTx,
        processingTxMessage,
        processingTxHash,
        onFinishTask,
        showNewTransactionButton: showNewTransactionButton_,
        othersButtonsWhenConfirmed,
        othersButtonsWhenFailed,
    } = props;
    //--------------------------------------
    const nameForNewTransactionButton = props.nameForNewTransactionButton !== undefined ? props.nameForNewTransactionButton : 'New Transaction';
    const showNewTransactionButton = showNewTransactionButton_ !== undefined ? showNewTransactionButton_ : true;
    const nameForCloseButton = props.nameForCloseButton !== undefined ? props.nameForCloseButton : 'Close';
    //--------------------------------------
    const showViewTransactionsButton = props.showViewTransactionsButton !== undefined ? props.showViewTransactionsButton : true;
    //--------------------------------------
    const appStore = useAppStore();
    //--------------------------------------
    const router = useRouter();
    //--------------------------------------
    return (
        <Fragment>
            <table className={styles.tableForm}>
                <tbody>
                    <tr>
                        <td>
                            <div className={styles.transaction}>
                                {isProcessingTx ? (
                                    <>
                                        <div className={styles.textPart}>
                                            <LoaderButton />
                                            <h2 className={styles.title}>PROCESSING YOUR TRANSACTION</h2>
                                            <p className={styles.text}>{processingTxMessage}</p>
                                            {processingTxHash !== undefined && processingTxHash !== '' ? (
                                                <>
                                                    <div className={styles.itemWidthIcons}>
                                                        <p>Tx Hash: {formatHash(processingTxHash)}</p>
                                                        <CopyButton content={processingTxHash} />
                                                        <OpenInNewTabButton
                                                            url={
                                                                appStore.siteSettings !== undefined
                                                                    ? `${appStore.siteSettings.getblockfrost_url_explorer_tx(processingTxHash)}`
                                                                    : ``
                                                            }
                                                        />
                                                    </div>
                                                </>
                                            ) : null}
                                        </div>
                                    </>
                                ) : isConfirmedTx ? (
                                    <>
                                        <div className={styles.textPart}>
                                            <SuccessIcon />
                                            <h2 className={styles.title}>CONGRATULATIONS</h2>
                                            <p className={styles.text}>
                                                We are thrilled to inform you that your recent transaction on our crypto app has been completed successfully!
                                            </p>
                                            {processingTxHash !== undefined && processingTxHash !== '' ? (
                                                <>
                                                    <div className={styles.itemWidthIcons}>
                                                        <p>Tx Hash: {formatHash(processingTxHash)}</p>
                                                        <CopyButton content={processingTxHash} />
                                                        <OpenInNewTabButton
                                                            url={
                                                                appStore.siteSettings !== undefined
                                                                    ? `${appStore.siteSettings!.getblockfrost_url_explorer_tx(processingTxHash)}`
                                                                    : ``
                                                            }
                                                        />
                                                    </div>
                                                </>
                                            ) : null}
                                            <p className={styles.text}>What do you want to do next?</p>
                                        </div>
                                        <div className={styles.buttonPart}>
                                            <div className={styles.buttonsBelowForm}>
                                                {showNewTransactionButton ? (
                                                    <Button mode="action" size="large" width={200} onClick={() => setIsConfirmedTx(false)}>
                                                        {nameForNewTransactionButton}
                                                    </Button>
                                                ) : null}
                                                {othersButtonsWhenConfirmed}
                                                {showViewTransactionsButton === true && (
                                                    <Button
                                                        mode="action"
                                                        size="large"
                                                        onClick={async () => {
                                                            await router.push({
                                                                pathname: ROUTES.PORTFOLIO,
                                                                query: { Task: TASK.TRANSACTIONS },
                                                            });
                                                        }}
                                                    >
                                                        View Transactions
                                                    </Button>
                                                )}
                                                <Button mode="action" size="large" onClick={() => onFinishTask?.(true)}>
                                                    {nameForCloseButton}
                                                </Button>
                                            </div>
                                        </div>
                                    </>
                                ) : isFaildedTx ? (
                                    <>
                                        <div className={styles.textPart}>
                                            <FailedIcon />
                                            <h2 className={styles.title}>SOMETHING WENT WRONG</h2>
                                            <p className={styles.text}>We are very sorry to inform you that your recent transaction has failed! You can try again!</p>
                                            <p className={styles.text}>{processingTxMessage}</p>
                                        </div>
                                        <div className={styles.buttonPart}>
                                            <div className={styles.buttonsBelowForm}>
                                                <Button mode="action" size="large" width={200} onClick={() => setIsFaildedTx(false)}>
                                                    Try Again
                                                </Button>
                                                {othersButtonsWhenFailed}
                                                <Button mode="action" size="large" onClick={() => onFinishTask?.(true)}>
                                                    {nameForCloseButton}
                                                </Button>
                                            </div>
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </Fragment>
    );
};

export default TxProcessing;
